import { useEffect, useRef } from "react";
import { OAuthButtons } from "./parts/OAuthButtons";
import { OAuthMethodBanner } from "./parts/OAuthMethodBanner";
import { OrDivider } from "./parts/OrDivider";
import { EmailField } from "./parts/EmailField";
import { PasswordField } from "./parts/PasswordField";
import { LockedEmailRow } from "./parts/LockedEmailRow";
import { SubmitButton } from "./parts/SubmitButton";
import { useSignInFlow } from "./useSignInFlow";

export function SignInForm() {
  const flow = useSignInFlow();
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (flow.step === "password") {
      const t = setTimeout(() => passwordRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [flow.step]);

  const disabled = flow.pending !== "none";

  if (flow.step === "email") {
    return (
      <>
        <OAuthButtons
          onSelect={flow.startOauth}
          pending={flow.pending}
          disabled={disabled}
        />
        <OrDivider />
        <form onSubmit={flow.submitEmail}>
          <EmailField
            email={flow.email}
            onChange={flow.setEmail}
            error={flow.emailError}
          />
          {flow.oauthOnly && (
            <OAuthMethodBanner
              state={flow.oauthOnly}
              email={flow.email}
              onContinue={flow.startOauth}
              pending={flow.pending}
              disabled={disabled}
            />
          )}
          {flow.formError && <div className="signin-error">{flow.formError}</div>}
          {!flow.oauthOnly && (
            <SubmitButton
              pending={flow.pending === "checking"}
              disabled={disabled}
              pendingLabel="Checking…"
            >
              Continue
            </SubmitButton>
          )}
        </form>
      </>
    );
  }

  return (
    <form onSubmit={flow.submitPassword}>
      <LockedEmailRow email={flow.email} onChangeEmail={flow.backToEmail} />
      <PasswordField
        ref={passwordRef}
        password={flow.password}
        onChange={flow.setPassword}
        error={flow.passwordError}
        forgotPasswordTo={`/sign-in/forgot-password?email=${encodeURIComponent(flow.email)}`}
      />
      {flow.formError && <div className="signin-error">{flow.formError}</div>}
      <SubmitButton
        pending={flow.pending === "password"}
        disabled={disabled}
        pendingLabel="Signing in…"
      >
        Sign in
      </SubmitButton>
    </form>
  );
}
